import React, { useState, useEffect } from 'react';
import './ThresholdModal.css';

interface ThresholdModalProps {
  autoApproveThreshold: number;
  autoRejectThreshold: number;
  onSave: (autoApprove: number, autoReject: number) => void;
  onClose: () => void;
}

const ThresholdModal: React.FC<ThresholdModalProps> = ({
  autoApproveThreshold,
  autoRejectThreshold,
  onSave,
  onClose
}) => {
  const [autoApprove, setAutoApprove] = useState(autoApproveThreshold);
  const [autoReject, setAutoReject] = useState(autoRejectThreshold);

  useEffect(() => {
    setAutoApprove(autoApproveThreshold);
    setAutoReject(autoRejectThreshold);
  }, [autoApproveThreshold, autoRejectThreshold]);

  const handleSave = () => {
    if (autoApprove <= autoReject) {
      alert('Auto approve threshold must be greater than auto reject threshold');
      return;
    }
    onSave(autoApprove, autoReject);
  };

  const handleAutoRejectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(Number(event.target.value), autoApprove - 1);
    setAutoReject(value);
  };

  const handleAutoApproveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(Number(event.target.value), autoReject + 1);
    setAutoApprove(value);
  };

  return (
    <div className="threshold-modal-overlay" onClick={onClose}>
      <div className="threshold-modal" onClick={(e) => e.stopPropagation()}>
        <div className="threshold-modal-header">
          <h2>Moderation Thresholds</h2>
          <button className="close-button" onClick={onClose}>
            x
          </button>
        </div>

        <div className="threshold-modal-content">
          <div className="threshold-intro">
            <p>Configure the score ranges for automatic approval, rejection, and manual review.</p>
          </div>

          <div className="threshold-editor">
            <div className="threshold-input">
              <div className="threshold-row">
                <label htmlFor="autoReject">Auto reject threshold</label>
                <span>&lt; {autoReject}</span>
              </div>
              <input
                id="autoReject"
                type="range"
                min={0}
                max={100}
                value={autoReject}
                onChange={handleAutoRejectChange}
              />
            </div>

            <div className="threshold-input">
              <div className="threshold-row">
                <label htmlFor="autoApprove">Auto approve threshold</label>
                <span>&ge; {autoApprove}</span>
              </div>
              <input
                id="autoApprove"
                type="range"
                min={0}
                max={100}
                value={autoApprove}
                onChange={handleAutoApproveChange}
              />
            </div>

            <div className="threshold-summary">
              <div className="summary-item">
                <span>Auto reject</span>
                <strong>&lt; {autoReject}</strong>
              </div>
              <div className="summary-item">
                <span>Pending review</span>
                <strong>
                  {autoReject} - {autoApprove - 1}
                </strong>
              </div>
              <div className="summary-item">
                <span>Auto approve</span>
                <strong>&ge; {autoApprove}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="threshold-modal-footer">
          <button className="cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button className="save-button" onClick={handleSave}>
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThresholdModal;

